import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, X } from 'lucide-react';
import { fetchSuggestions } from '@/services/searchService';
import { useTranslation } from 'react-i18next';

interface SearchBoxProps {
  className?: string;
  variant?: 'default' | 'header';
  placeholder?: string;
  onSearch?: (query: string) => void;
}

export function SearchBox({
  className = '',
  variant = 'default',
  placeholder,
  onSearch
}: SearchBoxProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  
  // Thiết lập placeholder mặc định nếu không được cung cấp
  const defaultPlaceholder = t('search_placeholder');
  const actualPlaceholder = placeholder || defaultPlaceholder;
  
  // Lấy suggestion khi người dùng gõ
  useEffect(() => {
    const fetchSuggestionsData = async () => {
      if (searchQuery.trim().length >= 2) {
        const data = await fetchSuggestions(searchQuery);
        setSuggestions(data);
      } else {
        setSuggestions([]);
      }
    };
    
    const timeoutId = setTimeout(() => {
      fetchSuggestionsData();
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);
  
  // Xử lý click bên ngoài để đóng suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Xử lý khi submit form search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) return;
    
    if (onSearch) {
      onSearch(searchQuery);
    } else {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
    
    setShowSuggestions(false);
  };
  
  // Xử lý khi chọn suggestion
  const handleSelectSuggestion = (suggestion: string) => {
    setSearchQuery(suggestion);
    if (onSearch) {
      onSearch(suggestion);
    } else {
      navigate(`/search?q=${encodeURIComponent(suggestion)}`);
    }
    setShowSuggestions(false);
  };
  
  // Phương thức xóa query
  const clearQuery = () => {
    setSearchQuery('');
    setSuggestions([]);
  };
  
  return (
    <div className={`relative ${className}`} ref={searchBoxRef}>
      <form onSubmit={handleSearch} className="flex items-center">
        <div className="relative flex-grow">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder={actualPlaceholder}
            className={`${
              variant === 'default'
                ? 'pl-10 pr-12 py-6 rounded-l-full rounded-r-none border-r-0 bg-white dark:bg-slate-900'
                : 'pl-8 pr-8 py-2 rounded-l-lg rounded-r-none border-r-0'
            }`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setShowSuggestions(true)}
          />
          {searchQuery && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={`absolute right-2 top-1/2 -translate-y-1/2 ${
                variant === 'default' ? 'h-7 w-7' : 'h-5 w-5'
              }`}
              onClick={clearQuery}
            >
              <X className={variant === 'default' ? 'h-3 w-3' : 'h-3 w-3'} />
            </Button>
          )}
        </div>
        <Button
          type="submit"
          className={
            variant === 'default'
              ? 'rounded-r-full rounded-l-none py-6'
              : 'rounded-r-lg rounded-l-none'
          }
        >
          {t('search')}
        </Button>
      </form>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-white dark:bg-slate-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-80 overflow-y-auto">
          <ul className="py-1">
            {suggestions.map((suggestion, index) => (
              <li
                key={index}
                className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-slate-800 cursor-pointer flex items-center"
                onClick={() => handleSelectSuggestion(suggestion)}
              >
                <Search className="h-3 w-3 mr-2 text-muted-foreground" />
                <span>{suggestion}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
} 